import { useCallback, useEffect, useState } from 'react'
import { api } from './api.ts'
import {
  PREFERENCE_KEY,
  cacheDivergenceOnly,
  cachedDivergenceOnly,
  fromPreferences,
} from './divergenceOnlyPreference.ts'

function browserStorage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

/** The divergence-only choice: this browser's copy first, then the account's. */
export function useDivergenceOnly(): readonly [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => cachedDivergenceOnly(browserStorage()))

  useEffect(() => {
    let live = true
    void api
      .preferences()
      .then((answer) => {
        const saved = fromPreferences(answer.preferences)
        if (!live || saved === undefined) return
        cacheDivergenceOnly(browserStorage(), saved)
        setOn(saved)
      })
      // A preference that cannot be read costs the default, never the screen.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const choose = useCallback((next: boolean) => {
    setOn(next)
    cacheDivergenceOnly(browserStorage(), next)
    // Not awaited: the screen already shows the choice, and a failed write
    // costs the next visit's starting point rather than this one.
    void api.savePreference(PREFERENCE_KEY, next).catch(() => undefined)
  }, [])

  return [on, choose] as const
}
